import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Box,
  Skeleton,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';

export interface AuditQuestionCardSkeletonProps {
  variant?: 'collapsed' | 'expanded';
  responseCount?: number;
  animate?: boolean;
  isMobile?: boolean;
}

export const AuditQuestionCardSkeleton: React.FC<AuditQuestionCardSkeletonProps> = ({
  variant = 'collapsed',
  responseCount = 3,
  animate = true,
  isMobile = false,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isActuallyMobile = isMobile || isSmallScreen;

  return (
    <Card
      elevation={1}
      sx={{
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <CardContent sx={{ p: isActuallyMobile ? 2 : 3 }}>
        {/* Question Header */}
        <Box
          sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Skeleton
                variant="text"
                width={isActuallyMobile ? 120 : 180}
                height={28}
                animation={animate ? 'wave' : false}
                aria-label="Loading question number"
              />
              <Skeleton
                variant="rectangular"
                width={100}
                height={24}
                sx={{ borderRadius: 2 }}
                animation={animate ? 'wave' : false}
                aria-label="Loading status"
              />
            </Box>
            {/* Question Title - 2 lines */}
            <Skeleton
              variant="text"
              width="75%"
              height={24}
              sx={{ mb: 0.5 }}
              animation={animate ? 'wave' : false}
              aria-label="Loading question title"
            />
            <Skeleton
              variant="text"
              width="60%"
              height={24}
              sx={{ mb: 2 }}
              animation={animate ? 'wave' : false}
            />
            {/* Question Metadata */}
            <Skeleton
              variant="text"
              width={isActuallyMobile ? 180 : 250}
              height={16}
              animation={animate ? 'wave' : false}
            />
          </Box>
        </Box>

        {/* Response Summary */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Skeleton
            variant="rectangular"
            width={120}
            height={24}
            sx={{ borderRadius: 2 }}
            animation={animate ? 'wave' : false}
            aria-label="Loading inclusion summary"
          />
          <Skeleton variant="text" width={80} height={20} animation={animate ? 'wave' : false} />
        </Box>

        {/* Expanded: Show Response Skeletons */}
        {variant === 'expanded' && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mt: 2 }}>
              <Skeleton
                variant="text"
                width={150}
                height={20}
                sx={{ mb: 2 }}
                animation={animate ? 'wave' : false}
                aria-label="Loading responses header"
              />

              {/* Individual Response Skeletons */}
              {Array.from({ length: responseCount }, (_, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    mb: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box sx={{ flex: 1, mr: 2 }}>
                      {/* Response value */}
                      <Skeleton
                        variant="text"
                        width="90%"
                        height={20}
                        sx={{ mb: 1 }}
                        animation={animate && index < 2 ? 'wave' : false}
                        aria-label="Loading response value"
                      />
                      {/* Optional comment (every other response) */}
                      {index % 2 === 0 && (
                        <Skeleton
                          variant="text"
                          width="85%"
                          height={16}
                          animation={animate && index < 2 ? 'wave' : false}
                          aria-label="Loading comment"
                        />
                      )}
                    </Box>
                    {/* Toggle button */}
                    <Skeleton
                      variant="circular"
                      width={40}
                      height={40}
                      animation={animate && index < 2 ? 'wave' : false}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}
      </CardContent>

      {/* Card Actions - Expand/Collapse Button */}
      <CardActions sx={{ px: isActuallyMobile ? 2 : 3, pb: isActuallyMobile ? 2 : 3 }}>
        <Skeleton
          variant="rectangular"
          width={150}
          height={36}
          sx={{ borderRadius: 1 }}
          animation={animate ? 'wave' : false}
          aria-label="Loading expand button"
        />
      </CardActions>
    </Card>
  );
};
